// "Already in the books" — batch 11 R1 (COA-D9, stage one: nothing is blocked; design §3.5.5).
//
// A document whose entry is posted can still be edited or voided until R3, and the books then go on
// stating the old figures. This round only SAYS so: the document's own GET carries `inBooks` (the entries
// still in the books — server/src/services/inBooks.ts, posted with no posted reversal) and every window
// that shows or edits the document draws this banner from it.
//
// ⛔ READ-ONLY. No button, no link that writes: Edit, Void and every other control keep exactly the state
// they had (R1 proof 4). A screen never re-derives "in the books" — it shows what the server answered.
// Batch 11 R3 (COA-D9, stage two): the lock now refuses, so the STAFF banner may carry the way on — the host's
// `action` per entry ("Reverse JV-…", CompanyWorkspace/reverseEntryBtn.tsx, which opens the document's entries
// panel on that entry). The banner itself still writes nothing, and the client's never carries an action.
// ⛔ TWO AUDIENCES, and the caller says which. Staff read the entry numbers, what each did and the remedy
// (reverse it in Journal entries). A client of the portal cannot reverse anything, so their words give the
// numbers and send them to their accountant — the portal never says "reverse" (§3.5.5).
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { InBooksRow } from '../../types';

const box: React.CSSProperties = {
  display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 12.5, lineHeight: 1.55,
  color: '#1E3A8A', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 11px',
};

const fmtDay = (iso: string): string => (/^\d{4}-\d{2}-\d{2}$/.test(String(iso || '')) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : String(iso || ''));
/** The statutory journals' short names (src/i18n/accounting.ts pa_jnl_*), for a row named by where it is printed. */
const IB_BOOK_KEY: Record<string, string> = { cash: 'pa_jnl_cash', bank: 'pa_jnl_bank', purchase: 'pa_jnl_purchase', sale: 'pa_jnl_sale', general: 'pa_jnl_general' };
const BLOCKED_NOTE_KEY: Record<string, string> = {
  receipts: 'aw_ib_note_receipts', notes: 'aw_ib_note_notes', filed: 'aw_ib_note_filed',
};
export function InBooksBanner({ rows, who, style, action, blocked }: {
  rows?: InBooksRow[] | null;
  /** (W2 ship) What keeps the document's figures as they are whatever is reversed ('receipts' / 'notes' / 'filed'): the
   *  staff note then points to a credit or debit note, never to "reverse first". */
  blocked?: string | null;
  /** 'staff' — the firm's workspace; 'client' — the client's own portal. */
  who: 'staff' | 'client';
  style?: React.CSSProperties;
  /** Staff only (batch 11 R3): the way on beside each entry — the workspace host passes ReverseEntryBtn. Ignored for a
   *  client. */
  action?: (row: InBooksRow) => React.ReactNode;
}) {
  const { t } = useTranslation();
  const list = (rows || []).filter(r => r && r.entryId);
  if (!list.length) return null;
  // (W1 ship) An entry made for this source BY HAND whose source has changed since it was posted (the server's
  // `handChanged`, services/handBooks.ts): its figures were typed and do not follow the source. Said, never acted on.
  const changed = list.filter(r => r.handChanged);
  if (who === 'client') {
    return (
      <div role="status" data-inbooks="client" style={{ ...box, ...style }}>
        <i className="ti ti-lock" aria-hidden style={{ fontSize: 15, marginTop: 1 }} />
        <div>
          <div style={{ fontWeight: 700 }}>{t('p_ib_title')}</div>
          <div>{t('p_ib_note', { docNos: list.map(r => r.docNo).join(', ') })}</div>
          {!!changed.length && (
            <div data-inbooks-hand-changed="client" style={{ color: '#92400E', marginTop: 2 }}>
              {t('p_ib_hand_changed', { docNos: changed.map(r => r.docNo).join(', ') })}
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div role="status" data-inbooks="staff" style={{ ...box, ...style }}>
      <i className="ti ti-lock" aria-hidden style={{ fontSize: 15, marginTop: 1 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700 }}>{t('aw_ib_title')}</div>
        <ul style={{ margin: '2px 0 4px', paddingLeft: 18 }}>
          {list.map(r => (
            <li key={r.entryId} data-inbooks-entry={r.docNo} data-inbooks-where={r.where ? `${r.where.book}:${r.where.page}` : undefined}>
              {t(r.role === 'settlement' ? 'aw_ib_row_settlement' : 'aw_ib_row_recognition', { docNo: r.where
                // (Batch 11 W4 P9, LB-16 / LB-24) Two entries by one number (a settlement keeps its invoice's): each named by where
                // it is printed — the book, the bank account, the page.
                ? `${r.docNo} ${t('aw_ib_where', { book: t(IB_BOOK_KEY[r.where.book] || r.where.book), account: r.where.account ? ` ${r.where.account}` : '', page: r.where.page })}`
                : r.docNo })}
              {r.reversalDraft ? <span style={{ color: '#92400E' }}> {t('aw_ib_reversal_draft', { rv: r.reversalDraft })}</span> : null}
              {/* (W2 ship, S4) Posted in a period closed since: it is not reversed — said, and no "Reverse" beside it. */}
              {!r.reversalDraft && r.closed ? <span style={{ color: '#92400E' }} data-inbooks-closed={r.docNo}> {t('aw_ib_closed', { from: fmtDay(r.closed.start), to: fmtDay(r.closed.end) })}</span> : null}
              {action && !(!r.reversalDraft && r.closed) ? <span style={{ marginLeft: 8 }}>{action(r)}</span> : null}
              {r.handChanged ? (
                <div data-inbooks-hand-changed={r.docNo} style={{ color: '#92400E', fontWeight: 600 }}>
                  <i className="ti ti-alert-triangle" aria-hidden /> {t('aw_ib_hand_changed', { docNo: r.docNo })}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        <div data-inbooks-note={blocked || 'locked'}>{t(BLOCKED_NOTE_KEY[blocked || ''] || 'aw_ib_note')}</div>
      </div>
    </div>
  );
}

export default InBooksBanner;
